import React, { useEffect, useState } from 'react';
import { Save, AlertCircle, CheckCircle2 } from 'lucide-react';
import AdminLayout from './AdminLayout';
import { getAdminConfig, updateAdminConfig } from '../../lib/admin';

export default function AdminBranding() {
    return <AdminConfigSection title="Apariencia" testIdPrefix="branding" fields={[
        { key: 'app_name', label: 'Nombre de la app', type: 'text' },
        { key: 'tagline', label: 'Subtítulo / tagline', type: 'text' },
        { key: 'logo_horizontal', label: 'URL del logo horizontal', type: 'url' },
        { key: 'logo_isotipo', label: 'URL del isotipo (cuadrado)', type: 'url' },
        { key: 'splash_background', label: 'URL del fondo del splash', type: 'url' },
        { key: 'theme.primary', label: 'Color primario', type: 'color' },
        { key: 'theme.primary_dark', label: 'Color primario oscuro', type: 'color' },
        { key: 'theme.primary_light', label: 'Color primario claro', type: 'color' },
        { key: 'theme.brand_navy', label: 'Color de texto principal', type: 'color' },
    ]} />;
}

export function AdminPlans() {
    return <AdminConfigSection title="Planes & Precios" testIdPrefix="plans" fields={[
        { key: 'premium.currency', label: 'Moneda', type: 'text', placeholder: 'USD' },
        { key: 'premium.monthly_price', label: 'Precio mensual', type: 'number' },
        { key: 'premium.annual_price', label: 'Precio anual', type: 'number' },
        { key: 'premium.lifetime_price', label: 'Precio vitalicio (lifetime)', type: 'number' },
    ]} />;
}

export function AdminLimits() {
    return <AdminConfigSection title="Límites del Plan Gratuito" testIdPrefix="limits" fields={[
        { key: 'free_limits.max_family_members', label: 'Máximo miembros por familia', type: 'number' },
        { key: 'free_limits.max_events_per_month', label: 'Máximo eventos por mes', type: 'number' },
        { key: 'free_limits.max_lists', label: 'Máximo listas', type: 'number' },
        { key: 'free_limits.max_image_mb', label: 'Almacenamiento de imágenes (MB)', type: 'number' },
        { key: 'free_limits.max_savings_goals', label: 'Máximo objetivos de ahorro', type: 'number' },
    ]} />;
}

export function AdminAds() {
    return <AdminConfigSection title="Anuncios" testIdPrefix="ads" fields={[
        { key: 'ads.banner_enabled', label: 'Mostrar banner publicitario', type: 'boolean' },
        { key: 'ads.interstitial_enabled', label: 'Mostrar interstitials (anuncios de pantalla completa)', type: 'boolean' },
        { key: 'ads.interstitial_frequency', label: 'Frecuencia de interstitial (cada N acciones)', type: 'number' },
    ]} />;
}

function AdminConfigSection({ title, fields, testIdPrefix }) {
    const [config, setConfig] = useState(null);
    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);
    const [status, setStatus] = useState(null);

    useEffect(() => {
        getAdminConfig().then((c) => {
            setConfig(c);
            const init = {};
            fields.forEach((f) => { init[f.key] = getByPath(c, f.key); });
            setForm(init);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onChange = (key, value) => setForm({ ...form, [key]: value });

    const save = async () => {
        setSaving(true);
        setStatus(null);
        try {
            // Build nested partial object from dotted keys
            const partial = {};
            for (const f of fields) {
                const value = form[f.key];
                if (value === undefined || value === '' || value === null) continue;
                const parts = f.key.split('.');
                if (parts.length === 1) {
                    partial[parts[0]] = castValue(value, f.type);
                } else {
                    partial[parts[0]] = partial[parts[0]] || {};
                    partial[parts[0]][parts[1]] = castValue(value, f.type);
                }
            }
            const updated = await updateAdminConfig(partial);
            setConfig(updated);
            setStatus({ ok: true, msg: 'Guardado ✨' });
        } catch (e) {
            setStatus({ ok: false, msg: e?.response?.data?.detail || 'Error al guardar' });
        } finally {
            setSaving(false);
            setTimeout(() => setStatus(null), 3000);
        }
    };

    return (
        <AdminLayout title={title}>
            {!config ? (
                <div className="py-20 flex justify-center"><div className="w-8 h-8 border-2 border-brand/30 border-t-brand rounded-full animate-spin" /></div>
            ) : (
                <div className="bg-white rounded-2xl border border-gray-200 p-6 max-w-2xl">
                    <div className="space-y-5">
                        {fields.map((f) => (
                            <FieldRow
                                key={f.key}
                                field={f}
                                value={form[f.key]}
                                onChange={(v) => onChange(f.key, v)}
                                testIdPrefix={testIdPrefix}
                            />
                        ))}
                    </div>

                    {status && (
                        <div className={`mt-5 flex items-center gap-2 text-sm font-semibold ${status.ok ? 'text-emerald-600' : 'text-red-600'}`} data-testid={`${testIdPrefix}-status`}>
                            {status.ok ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                            {status.msg}
                        </div>
                    )}

                    <button
                        onClick={save}
                        disabled={saving}
                        className="mt-6 h-11 px-6 bg-brand text-white rounded-full font-bold flex items-center gap-2 disabled:opacity-50"
                        data-testid={`${testIdPrefix}-save`}
                    >
                        <Save className="w-4 h-4" />
                        {saving ? 'Guardando…' : 'Guardar cambios'}
                    </button>
                </div>
            )}
        </AdminLayout>
    );
}

function FieldRow({ field, value, onChange, testIdPrefix }) {
    const testId = `${testIdPrefix}-${field.key.replace(/\./g, '-')}`;
    if (field.type === 'boolean') {
        return (
            <div className="flex items-center justify-between">
                <label className="text-sm font-semibold text-gray-800">{field.label}</label>
                <button
                    onClick={() => onChange(!value)}
                    className={`relative w-11 h-6 rounded-full transition ${value ? 'bg-brand' : 'bg-gray-300'}`}
                    data-testid={testId}
                >
                    <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition ${value ? 'left-5' : 'left-0.5'}`} />
                </button>
            </div>
        );
    }
    if (field.type === 'color') {
        return (
            <div>
                <label className="text-sm font-semibold text-gray-800 block mb-1.5">{field.label}</label>
                <div className="flex items-center gap-2">
                    <input
                        type="color"
                        value={value || '#000000'}
                        onChange={(e) => onChange(e.target.value)}
                        className="w-12 h-11 rounded-lg border border-gray-200"
                        data-testid={testId}
                    />
                    <input
                        type="text"
                        value={value || ''}
                        onChange={(e) => onChange(e.target.value)}
                        className="flex-1 h-11 px-3 bg-white border border-gray-200 rounded-xl text-sm font-mono"
                        placeholder="#000000"
                    />
                </div>
            </div>
        );
    }
    return (
        <div>
            <label className="text-sm font-semibold text-gray-800 block mb-1.5">{field.label}</label>
            <input
                type={field.type}
                value={value ?? ''}
                onChange={(e) => onChange(e.target.value)}
                placeholder={field.placeholder}
                className="w-full h-11 px-3 bg-white border border-gray-200 rounded-xl text-sm focus:border-brand focus:ring-2 focus:ring-brand/20"
                data-testid={testId}
            />
        </div>
    );
}

function getByPath(obj, path) {
    return path.split('.').reduce((o, k) => (o ? o[k] : undefined), obj);
}
function castValue(v, type) {
    if (type === 'number') return Number(v);
    if (type === 'boolean') return Boolean(v);
    return v;
}
